import { createFileRoute, Link } from "@tanstack/react-router";
import type { HistoryState } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { PatrolMap } from "@/components/PatrolMap";
import { SiteScopeSheet, type ScopeCategory } from "@/components/dashboards/SiteScopeSheet";
import { useLiveSiteOps } from "@/lib/dashboard/use-live-site-ops";
import { fromDashboard } from "@/lib/dashboard-origin";
import { LiveOpsStrip } from "@/components/dashboards/LiveOpsStrip";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Users, Briefcase, AlertTriangle, ShieldCheck, ShieldAlert, Bell,
  ChevronRight, MapPin, QrCode, ClipboardList,
} from "lucide-react";


const ALLOWED = ["supervisor", "site_supervisor"];

export const Route = createFileRoute("/_authenticated/supervisor")({
  component: SupervisorDashboard,
  head: () => ({ meta: [{ title: "Supervisor Dashboard — MissionReady" }] }),
});

type Site = { id: string; name: string; latitude: number | null; longitude: number | null };

function SupervisorDashboard() {
  const { currentOrg, currentRole, user, isPlatformAdmin } = useAuth();
  const allowed = isPlatformAdmin || (currentRole && ALLOWED.includes(currentRole));
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState<string>("all");
  const [scope, setScope] = useState<ScopeCategory | null>(null);
  const [c, setC] = useState({ guards: 0, onDuty: 0, openShifts: 0, incidents: 0, offPerim: 0, alerts: 0 });

  // Load sites the supervisor is assigned to
  useEffect(() => {
    if (!currentOrg || !user) return;
    (async () => {
      const { data: assigns } = await supabase
        .from("user_site_assignments")
        .select("site_id")
        .eq("organization_id", currentOrg.id)
        .eq("user_id", user.id);
      const siteIds = (assigns ?? []).map((a: any) => a.site_id).filter(Boolean);
      if (siteIds.length === 0) {
        // Fallback for unassigned supervisors: show all sites in org
        const { data } = await supabase.from("sites")
          .select("id,name,latitude,longitude")
          .eq("organization_id", currentOrg.id).eq("active", true);
        setSites((data ?? []) as Site[]);
        return;
      }
      const { data } = await supabase.from("sites")
        .select("id,name,latitude,longitude")
        .in("id", siteIds);
      setSites((data ?? []) as Site[]);
    })();
  }, [currentOrg?.id, user?.id]);

  const visibleSiteIds = useMemo(
    () => (siteId === "all" ? sites.map((s) => s.id) : [siteId]),
    [sites, siteId],
  );

  useEffect(() => {
    if (!currentOrg || visibleSiteIds.length === 0) {
      setC({ guards: 0, onDuty: 0, openShifts: 0, incidents: 0, offPerim: 0, alerts: 0 });
      return;
    }
    const orgId = currentOrg.id;
    (async () => {
      const [shifts, openShifts, incs, perim, alerts] = await Promise.all([
        supabase.from("shifts").select("officer_id", { count: "exact" }).eq("organization_id", orgId).in("site_id", visibleSiteIds).eq("status", "active"),
        supabase.from("shifts").select("*", { count: "exact", head: true }).eq("organization_id", orgId).in("site_id", visibleSiteIds).is("officer_id", null).eq("status", "scheduled"),
        supabase.from("incidents").select("*", { count: "exact", head: true }).eq("organization_id", orgId).in("site_id", visibleSiteIds).in("status", ["open", "in_progress"]),
        supabase.from("geofence_events").select("*", { count: "exact", head: true }).eq("organization_id", orgId).eq("event_type", "exit").gte("occurred_at", new Date(Date.now() - 60 * 60 * 1000).toISOString()),
        supabase.from("incidents").select("*", { count: "exact", head: true }).eq("organization_id", orgId).in("site_id", visibleSiteIds).eq("status", "open"),
      ]);
      const guardSet = new Set((shifts.data ?? []).map((s: any) => s.officer_id).filter(Boolean));
      setC({
        guards: guardSet.size,
        onDuty: shifts.count ?? 0,
        openShifts: openShifts.count ?? 0,
        incidents: incs.count ?? 0,
        offPerim: perim.count ?? 0,
        alerts: alerts.count ?? 0,
      });
    })();
  }, [currentOrg?.id, visibleSiteIds.join(",")]);

  // Single-site organizations should not be asked to choose between "all" and
  // their only site — scope straight to it.
  useEffect(() => {
    if (sites.length === 1 && siteId === "all") setSiteId(sites[0].id);
  }, [sites, siteId]);

  const scopeLabel =
    sites.length === 1
      ? sites[0]?.name ?? "My site"
      : siteId === "all"
        ? "All my sites"
        : sites.find((s) => s.id === siteId)?.name ?? "Selected site";

  const live = useLiveSiteOps({
    orgId: currentOrg?.id,
    sites,
    siteIds: visibleSiteIds,
  });
  const originState = fromDashboard({ id: "supervisor", siteId, scopeLabel });


  if (!allowed) {
    return (
      <div className="px-4 py-10 text-center text-sm text-muted-foreground">
        You don't have access to the Supervisor Dashboard.
      </div>
    );
  }

  return (
    <div className="px-4 py-5 md:px-6 md:py-7">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h1 className="font-display text-2xl font-bold md:text-3xl">Supervisor Dashboard</h1>
        </div>
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Your assigned sites · {sites.length} site{sites.length === 1 ? "" : "s"}
        </p>
      </div>

      {sites.length > 1 ? (
        <div className="mb-5 rounded-2xl border border-border bg-card px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Site</span>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All my sites</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : (
        <div className="mb-5 rounded-2xl border border-border bg-card px-4 py-3 text-sm">
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Site</span>{" "}
          <span className="font-semibold">{scopeLabel}</span>
        </div>
      )}

      <section className="mb-6">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">My Site Overview</h2>
        <div className="grid grid-cols-2 gap-3">
          <Stat label="My Guards" value={c.guards} icon={Users} tone="default" onClick={() => setScope("guards")} />
          <Stat label="On Duty" value={c.onDuty} icon={ShieldCheck} tone="success" onClick={() => setScope("onDuty")} />
          <Stat label="Open Shifts" value={c.openShifts} icon={Briefcase} tone="warning" onClick={() => setScope("openShifts")} />
          <Stat label="Open Incidents" value={c.incidents} icon={AlertTriangle} tone="warning" onClick={() => setScope("incidents")} />
          <Stat label="Off Perimeter (1h)" value={c.offPerim} icon={ShieldAlert} tone="danger" onClick={() => setScope("offPerimeter")} />
          <Stat label="Open Alerts" value={c.alerts} icon={Bell} tone="danger" onClick={() => setScope("alerts")} />
        </div>
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Live Operations</h2>
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <div className="relative">
            <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-primary px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-white" /> Live Data
            </span>
            <PatrolMap
              geofences={live.fences}
              officers={live.officers}
              height={220}
              fitTo="geofences"
              intelligenceClassName="absolute left-3 top-12 z-[400] inline-flex min-h-9 items-center gap-1.5 rounded-md border border-[#C0C0C0]/40 bg-card/90 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#C0C0C0] backdrop-blur transition-colors hover:bg-[#222]"
            />
          </div>
          <LiveOpsStrip
            summary={live.summary}
            loading={live.loading}
            error={live.error}
            scopeLabel={scopeLabel}
          />
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <div className="font-display text-base font-semibold">Live Map</div>
              <div className="text-xs text-muted-foreground">Real-time officer GPS · perimeter status</div>
            </div>
            <Link
              to="/operations/live"
              state={originState}
              className="inline-flex items-center gap-1 rounded-lg bg-foreground px-3 py-2 text-xs font-bold uppercase tracking-wider text-background"
            >
              Open <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Quick Access</h2>
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <QuickAction icon={Users} label="My Guards" onClick={() => setScope("guards")} />
          <QuickRow to="/schedule" icon={Briefcase} label="Today Shifts" search={{ view: "today", ...(siteId !== "all" ? { site: siteId } : {}) }} state={originState} />
          <QuickRow to="/sites" icon={MapPin} label="Sites" state={originState} />
          <QuickRow to="/guard-tours" icon={QrCode} label="Guard Tours" state={originState} />
          <QuickRow to="/reports" icon={ClipboardList} label="Reports" state={originState} />
        </div>
      </section>

      {currentOrg && (
        <SiteScopeSheet
          category={scope}
          orgId={currentOrg.id}
          siteIds={visibleSiteIds}
          scopeLabel={scopeLabel}
          originState={originState}
          onClose={() => setScope(null)}
        />
      )}

    </div>
  );
}

function Stat({
  label, value, icon: Icon, tone, onClick,
}: { label: string; value: number; icon: any; tone: "default" | "success" | "warning" | "danger"; onClick?: () => void }) {
  const toneCls =
    tone === "success" ? "bg-success text-success-foreground" :
    tone === "warning" ? "bg-warning text-warning-foreground" :
    tone === "danger"  ? "bg-destructive text-destructive-foreground" :
                         "bg-foreground text-background";
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-h-[44px] rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/50"
    >
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] leading-snug text-muted-foreground">{label}</span>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${toneCls}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 font-display text-3xl font-bold">{value}</div>
    </button>
  );
}

function QuickAction({ icon: Icon, label, onClick }: { icon: any; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[44px] w-full items-center justify-between gap-3 border-b border-border px-4 py-3.5 text-left last:border-b-0 hover:bg-muted/40"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-base font-medium">{label}</span>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

function QuickRow({ to, icon: Icon, label, search, state }: { to: string; icon: any; label: string; search?: Record<string, unknown>; state?: HistoryState }) {
  return (
    <Link
      to={to as never}
      search={search as never}
      state={state}
      className="flex items-center justify-between gap-3 border-b border-border px-4 py-3.5 last:border-b-0 hover:bg-muted/40"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-base font-medium">{label}</span>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </Link>
  );
}
